<script setup lang="ts">
import { ref, computed } from "vue";
import { HomeService } from "~/services/HomeService";
import BaseLink from "@/components/buttons/BaseLink.vue";
import {
  VITE_BANNER_BRAND_THIN,
  VITE_BANNER_BRAND_SQUARE,
} from "~/config/constants.js";

interface HomeBannerItem {
  id: number;
  image?: string;
  name: string;
  action_url?: string;
}

// state
const imagesThin = useState<HomeBannerItem[]>("home.imagesThin", () => []);
const imagesSquare = useState<HomeBannerItem[]>("home.imagesSquare", () => []);
const imageErrors = ref<{ [key: string]: boolean }>({});
const routeHydrated = useRouteHydrated();

// computed
const imagesThinToShow = computed(() => imagesThin.value.slice(0, 2));

const imagesSquareFirst = computed(() =>
  imagesSquare.value.length >= 1 ? imagesSquare.value[0] : null,
);

const imagesSquareSecond = computed(() =>
  imagesSquare.value.length >= 2
    ? imagesSquare.value[1]
    : imagesSquareFirst.value,
);

const homeService = new HomeService();
const init = async () => {
  if (!routeHydrated.value) {
    try {
      imagesThin.value = await homeService.getBanners(VITE_BANNER_BRAND_THIN);
      imagesSquare.value = await homeService.getBanners(
        VITE_BANNER_BRAND_SQUARE,
      );
    } catch (err) {
      console.error(err);
    }
  }
};

if (import.meta.server) {
  await init();
} else {
  init(); // en cliente mostramos pantallas de carga
}
</script>

<template>
  <section class="p-0 mt-5 d-none d-lg-flex">
    <div class="row g-0 w-100">
      <div class="col">
        <div class="p-1">
          <base-link
            v-if="imagesSquareFirst"
            :to="imagesSquareFirst.action_url || '#'"
          >
            <img
              v-if="
                imagesSquareFirst &&
                imagesSquareFirst.image &&
                !imageErrors[imagesSquareFirst.id || '']
              "
              :src="imagesSquareFirst.image"
              width="600"
              height="400"
              :alt="imagesSquareFirst.name"
              class="gallery height-gallery"
              loading="lazy"
              decoding="async"
              @error="imageErrors[imagesSquareFirst.id || ''] = true"
            />
            <div
              v-if="imagesSquareFirst && imageErrors[imagesSquareFirst.id]"
              class="gallery height-gallery border border-light-subtle d-flex justify-content-center align-items-center"
            >
              <span
                class="text-secondary fw-semibold text-capitalize fs-8 px-2 text-center"
              >
                {{ imagesSquareFirst.name }}
              </span>
            </div>
          </base-link>
          <div
            v-if="!imagesSquareFirst"
            class="gallery height-gallery border border-light-subtle banner-gallery-skeleton"
          />
        </div>
      </div>
      <div class="col">
        <div class="p-1">
          <div class="row row-gap-2">
            <base-link
              v-for="(image, index) in imagesThinToShow"
              :key="index"
              class="d-flex justify-content-center align-items-center"
              :to="image.action_url || '#'"
            >
              <img
                v-if="image.image && !imageErrors[index]"
                :src="image.image"
                width="600"
                height="400"
                :alt="image.name"
                class="gallery height-gallery-center"
                loading="lazy"
                decoding="async"
                @error="imageErrors[index] = true"
              />
              <div
                v-if="imageErrors[index]"
                class="gallery height-gallery-center border border-light-subtle d-flex justify-content-center align-items-center"
              >
                <span
                  class="text-secondary fw-semibold text-capitalize fs-8 px-2 text-center"
                >
                  {{ image.name }}
                </span>
              </div>
            </base-link>
            <div
              v-for="placeholder in 2 - imagesThinToShow.length"
              :key="`thin-placeholder-${placeholder}`"
              class="gallery height-gallery-center border border-light-subtle banner-gallery-skeleton"
            />
          </div>
        </div>
      </div>
      <div class="col">
        <div class="p-1">
          <base-link
            v-if="imagesSquareSecond"
            :to="imagesSquareSecond.action_url || '#'"
            class="w-100 h-100"
          >
            <img
              v-if="
                imagesSquareSecond &&
                imagesSquareSecond.image &&
                !imageErrors[imagesSquareSecond.id]
              "
              :src="imagesSquareSecond.image"
              width="450"
              height="250"
              :alt="imagesSquareSecond.name"
              class="gallery height-gallery"
              loading="lazy"
              decoding="async"
              @error="imageErrors[imagesSquareSecond.id] = true"
            />
            <div
              v-if="imagesSquareSecond && imageErrors[imagesSquareSecond.id]"
              class="gallery height-gallery border border-light-subtle d-flex justify-content-center align-items-center"
            >
              <span
                class="text-secondary fw-semibold text-capitalize fs-8 px-2 text-center"
              >
                {{ imagesSquareSecond.name }}
              </span>
            </div>
          </base-link>
          <div
            v-if="!imagesSquareSecond"
            class="gallery height-gallery border border-light-subtle banner-gallery-skeleton"
          ></div>
        </div>
      </div>
    </div>
  </section>
  <!-- <section class="row g-0 mt-5 d-none d-lg-flex">
        <div
            v-if="imagesSquareFirst"
            class="col"
        >
            <base-link
                v-if="imagesSquareFirst"
                :to="imagesSquareFirst.action_url || '#'"
            >
                <img
                    v-if="imagesSquareFirst && imagesSquareFirst.image && !imageErrors[imagesSquareFirst.id]"
                    v-lazy="imagesSquareFirst.image"
                    :alt="imagesSquareFirst.name"
                    class="gallery height-gallery"
                    @error="imageErrors[imagesSquareFirst.id] = true;"
                >
                <div
                    v-if="imagesSquareFirst && imageErrors[imagesSquareFirst.id]"
                    class="gallery height-gallery border border-light-subtle d-flex justify-content-center align-items-center"
                >
                    <span
                        class="text-secondary fw-semibold text-capitalize fs-8 px-2 text-center"
                    >
                        {{ imagesSquareFirst.name }}
                    </span>
                </div>
            </base-link>
            <div
                v-if="!imagesSquareFirst"
                class="gallery height-gallery border border-light-subtle"
            />
        </div>
        <div
            v-if="imagesThinToShow.length > 0"
            class="col"
        >
            <div class="row row-gap-2">
                <base-link
                    v-for="(image, index) in imagesThinToShow"
                    :key="index"
                    class="d-flex justify-content-center align-items-center"
                    :to="imagesThinToShow.action_url || '#'"
                >
                    <img
                        v-if="image.image && !imageErrors[index]"
                        v-lazy="image.image"
                        :alt="image.name"
                        class="gallery height-gallery-center"
                        @error="imageErrors[index] = true;"
                    >
                    <div
                        v-if="imageErrors[index]"
                        class="gallery height-gallery-center border border-light-subtle d-flex justify-content-center align-items-center"
                    >
                        <span
                            class="text-secondary fw-semibold text-capitalize fs-8 px-2 text-center"
                        >
                            {{ image.name }}
                        </span>
                    </div>
                </base-link>
            </div>
        </div>
        <div
            v-if="imagesSquareSecond"
            class="col "
        >

            <base-link
                v-if="imagesSquareSecond"
                :to="imagesSquareSecond.action_url || '#'"
                class="w-100 h-100"
            >
                <img
                    v-if="imagesSquareSecond && imagesSquareSecond.image && !imageErrors[imagesSquareSecond.id]"
                    v-lazy="imagesSquareSecond.image"
                    :alt="imagesSquareSecond.name"
                    class="gallery height-gallery"
                    @error="imageErrors[imagesSquareSecond.id] = true;"
                >
                <div
                    v-if="imagesSquareSecond && imageErrors[imagesSquareSecond.id]"
                    class="gallery height-gallery border border-light-subtle d-flex justify-content-center align-items-center"
                >
                    <span
                        class="text-secondary fw-semibold text-capitalize fs-8 px-2 text-center"
                    >
                        {{ imagesSquareSecond.name }}
                    </span>
                </div>
            </base-link>
            <div
                v-if="!imagesSquareSecond"
                class="gallery height-gallery border border-light-subtle"
            />
        </div>
    </section> -->
</template>

<style lang="sass" scoped>
.gallery
    width: 100%
    height: 100%
    object-position: center

.height-gallery
    height: auto
    max-height: 440px
    @media (min-width: 992px)
        min-height: 157px

    @media (min-width: 1200px)
        min-height: 200px

    @media (min-width: 1400px)
        min-height: 240px

    @media (min-width: 1700px)
        min-height: 440px

.height-gallery-center
    height: auto
    max-width: 596px

    @media (min-width: 992px)
        height: 75px

    @media (min-width: 1200px)
        min-height: 97px

    @media (min-width: 1400px)
        min-height: 116px

    @media (min-width: 1700px)
        min-height: 216px

.banner-gallery-skeleton
    background: linear-gradient(90deg, #f2f4f7 0%, #e9edf2 50%, #f2f4f7 100%)
    background-size: 200% 100%
    animation: bannerGalleryShimmer 1.4s ease-in-out infinite

@keyframes bannerGalleryShimmer
    0%
        background-position: 200% 0
    100%
        background-position: -200% 0
</style>
